/**
 * PRIVA Governance — Signal Catalog (Phase 6).
 *
 * Read-only reference of every SmartEWM signal and the SAP API behind it:
 * Signal · Domain · API Type · Service · Entity · Status. Metadata only —
 * never exposes credentials, secrets, tokens, headers, or URLs.
 */
import { useEffect, useState } from "react";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Loader2, ShieldCheck, BookOpen } from "lucide-react";
import { getSignalCatalog, extractError } from "../lib/privaAdminApi";

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function SignalCatalogPage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getSignalCatalog()
      .then((d) => setRows(d.catalog || []))
      .catch((e) => setError(extractError(e, "Failed to load signal catalog.")));
  }, []);

  return (
    <div className="p-6 max-w-[1100px] mx-auto" data-testid="signal-catalog-page">
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-1.5">
          <ShieldCheck className="w-4 h-4 text-[#025979]" />
          <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">PRIVA Governance</p>
        </div>
        <h1 className="text-xl font-bold text-[#111827] flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          <BookOpen className="w-5 h-5 text-[#025979]" /> Signal Catalog
        </h1>
        <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
          Every SmartEWM signal and the SAP API behind it. Use this reference to
          validate integrations and onboard consultants quickly.
        </p>
      </header>

      {error && <div className="text-red-600 text-xs py-2" data-testid="signal-catalog-error">{error}</div>}

      {!rows ? (
        <div className="text-center py-16"><Loader2 className="w-5 h-5 mx-auto animate-spin text-[#025979]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="text-[#475569]">Signal</TableHead>
                <TableHead className="text-[#475569]">Domain</TableHead>
                <TableHead className="text-[#475569]">API Type</TableHead>
                <TableHead className="text-[#475569]">Service</TableHead>
                <TableHead className="text-[#475569]">Entity</TableHead>
                <TableHead className="text-[#475569]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.signalType} data-testid={`signal-catalog-row-${r.signalType}`}>
                  <TableCell className="font-semibold text-[#111827]">{r.signal}</TableCell>
                  <TableCell className="text-[#374151]">{r.domain}</TableCell>
                  <TableCell className="text-[#374151]">{r.apiType}</TableCell>
                  <TableCell className="font-mono text-[12px] text-[#374151]">{r.serviceName}</TableCell>
                  <TableCell className="font-mono text-[12px] text-[#374151]">{r.entitySet}</TableCell>
                  <TableCell>
                    <Badge className={`border ${STATUS_STYLES[r.status] || "bg-gray-100 text-gray-600 border-gray-200"}`} data-testid={`signal-catalog-status-${r.signalType}`}>
                      {r.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
